export type TCloudflareTransformOptions = {
  /** Quality - between 1-100. Cloudflare defaults to 85.
   *
   * Docs: https://developers.cloudflare.com/images/transform-images/transform-via-url/#quality */
  q?: number;

  /**
   * Width
   *
   * Docs: https://developers.cloudflare.com/images/transform-images/transform-via-url/#width */
  w?: number;

  /**
   * Height
   *
   * Docs: https://developers.cloudflare.com/images/transform-images/transform-via-url/#height */
  h?: number;

  /** Affects interpretation of width and height - defaults to "scale-down"
   *
   * Docs: https://developers.cloudflare.com/images/transform-images/transform-via-url/#fit */
  fit?: "scale-down" | "contain" | "cover" | "crop" | "pad";

  /** Device Pixel Ratio
   *
   * Docs: https://developers.cloudflare.com/images/transform-images/transform-via-url/#dpr
   *
   * Values between 1 and 4 are common according to: https://web.dev/articles/codelab-density-descriptors#pixel_density
   * */
  dpr?: 1 | 1.5 | 2 | 2.5 | 3 | 3.5 | 4;

  /**
   * Gravity - When cropping with fit: "cover" and fit: "crop", this parameter defines the side or point that should not be cropped.
   *
   * Docs: https://developers.cloudflare.com/images/transform-images/transform-via-url/#gravity */
  gravity?: "auto" | "left" | "right" | "top" | "bottom" | `${number}x${number}`;

  /**
   * Format - The auto option will serve the WebP or AVIF format to browsers that support it. If this option is not specified, a standard format like JPEG or PNG will be used. Cloudflare will default to JPEG when possible due to the large size of PNG files.
   *
   * Docs: https://developers.cloudflare.com/images/transform-images/transform-via-url/#format
   */

  format?: "auto" | "webp" | "jpeg" | "png" | "avif";
};

const defaultCloudflareOptions = {
  fit: "scale-down",
  format: "auto",
} satisfies TCloudflareTransformOptions;

const CLOUDFLARE_TRANSFORM_PATH = "cdn-cgi/image";
const OPTIMIZELY_DXP_HOSTNAME = "dxcloud.episerver.net";

export const cloudflareTransform = (
  imageUrl: string | undefined | null,
  options?: TCloudflareTransformOptions
): string | undefined => {
  if (!imageUrl) {
    return undefined;
  }

  if (imageUrl.indexOf(OPTIMIZELY_DXP_HOSTNAME) > -1) {
    return imageUrl;
  }

  const mergedOptions = {
    ...defaultCloudflareOptions,
    ...options,
  };

  let optionsArr: Array<string> = [];
  for (const [key, value] of Object.entries(mergedOptions)) {
    optionsArr.push(`${key}=${value}`);
  }

  const url = new URL(imageUrl);
  const protocolHost = `${url.protocol}//${url.host}`;
  const originalImagePath = url.pathname;

  const transformedUrl = `${protocolHost}/${CLOUDFLARE_TRANSFORM_PATH}/${optionsArr.join(",")}${originalImagePath}`;
  return transformedUrl;
};

export const imageTransformer = {
  cloudflare: cloudflareTransform,
};

export const getImageLoading = (index: number | undefined) => {
  return index && index >= 2 ? "lazy" : "eager";
};
